import { useCallback, useEffect, useState } from "react"
import moment from "moment"
import { Flex, Button } from "antd"
import { MailOutlined, MessageOutlined, MobileOutlined } from "@ant-design/icons"
import { Title, Text } from "ui/Typography"
import { useDispatch, useSelector } from "react-redux"
import { getCommunicationHistory, cleanCommunicationHistory, COMMUNICATION_HISTORY_PAGE_SIZE } from "store/bookings"
import PropTypes from "prop-types"
import styles from "./index.module.css"

const TYPE_ICON = {
  Email: <MailOutlined />,
  Sms: <MobileOutlined />,
  In_app: <MessageOutlined />
}

const TYPE_LABEL = {
  Email: "Email",
  Sms: "SMS",
  In_app: null
}

const CommunicationHistory = ({ bookingId }) => {
  const dispatch = useDispatch()
  const { items, metadata } = useSelector((store) => store?.bookings?.communicationHistory) || { items: [], metadata: null }
  const [page, setPage] = useState(1)
  const fetch = useCallback(
    (p = 1) => {
      if (bookingId) dispatch(getCommunicationHistory(bookingId, { page: p, per_page: COMMUNICATION_HISTORY_PAGE_SIZE }))
    },
    [bookingId, dispatch]
  )

  useEffect(() => {
    fetch(1)
    setPage(1)
    return () => dispatch(cleanCommunicationHistory())
  }, [bookingId, dispatch]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleLoadMore = () => {
    const next = page + 1
    setPage(next)
    fetch(next)
  }

  const hasMore = metadata ? page < metadata.total_pages : false

  return (
    <Flex vertical gap={16} className={`${styles.container} p-xl`}>
      <Title level={4}>Communication History</Title>

      {items.length === 0 ? (
        <p className={styles.emptyText}>No communication has been sent yet.</p>
      ) : (
        <>
          <div className={styles.scrollArea}>
            <Flex vertical>
              {items.map((log) => {
                const typeLabel = TYPE_LABEL[log.type]
                return (
                  <Flex key={`${log.type}-${log.sent_at}`} gap={12} align="flex-start" className={styles.logItem}>
                    <div className={styles.iconWrapper}>{TYPE_ICON[log.type] ?? <MessageOutlined />}</div>
                    <Flex vertical gap={2} className={styles.logContent}>
                      <Flex align="center" gap={8}>
                        <Text className={styles.logTitle}>{log.title}</Text>
                      </Flex>
                      {(log.client_phone || log.client_email) && (
                        <Text className={styles.contactLine}>{log.client_phone || log.client_email}</Text>
                      )}
                      {log.sent_at && <Text className={styles.timestamp}>{moment.utc(log.sent_at).local().format("MM/DD/YYYY, hh:mm A")}</Text>}
                    </Flex>
                  </Flex>
                )
              })}
            </Flex>
          </div>

          {hasMore && (
            <Button type="link" className={styles.showMore} onClick={handleLoadMore}>
              Show more
            </Button>
          )}
        </>
      )}
    </Flex>
  )
}

CommunicationHistory.propTypes = {
  bookingId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
}

export default CommunicationHistory
